import React, { useState } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { useStore } from '@hooks';
import { Button, Icon, Typography } from '@ui';
import { copyToClipboard } from '@utils';
import { Flex, notification } from 'antd';
import { isInviteInProgress } from '../../../../utils/apiTokenState';
import { StyledCopyButton, StyledSteps } from '../../styled';

const KLICKLY_EMAIL = 'accounts@klickly.com';
const SHOW_STEPS_BUTTON_STYLE = { color: COLORS.$gray60Black, fontWeight: 500, fontSize: 16, lineHeight: '24px' };

/**
 * Klickly invite flow: invite steps, or the in-progress state once the invite was confirmed.
 * The invite confirmation is saved immediately through its own store action, not the page Save.
 * @param {object} props
 * @param {object} props.tripleWhaleSettings
 */
const KlicklyTab = ({ tripleWhaleSettings }) => {
    const { updateTripleWhaleInvite } = useStore().accountsStore;
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);
    const inviteSent = isInviteInProgress(tripleWhaleSettings);

    const setInviteSent = async (value) => {
        setSubmitting(true);
        const response = await updateTripleWhaleInvite(value);
        setSubmitting(false);
        if (response?.error) {
            notification.error({
                message: (
                    <span data-e2e='tracking_tw_invite_error_notification'>
                        We couldn’t save your request. Please try again.
                    </span>
                )
            });
        }
    };

    const copyEmail = async () => {
        await copyToClipboard(KLICKLY_EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    };

    if (inviteSent) {
        return (
            <React.Fragment>
                <Typography.Text type='body2' color={COLORS.$gray100Black} style={BOTTOM_INDENTS.XS}>
                    Klickly creates your API key...
                </Typography.Text>
                <Typography.Text type='body2' color={COLORS.$gray60Black} style={BOTTOM_INDENTS.M}>
                    In progress. Usually within 1 business day
                </Typography.Text>
                <Button
                    data-e2e='tracking_tw_show_steps_btn'
                    type='link'
                    style={SHOW_STEPS_BUTTON_STYLE}
                    loading={submitting}
                    onClick={() => setInviteSent(false)}
                >
                    Didn’t send it yet? Show the steps again
                </Button>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <StyledSteps>
                <li>
                    In Triple Whale, open <strong>Settings › Team › Invite Members</strong>.
                </li>
                <li>
                    <Flex align='center' gap={12} wrap>
                        <span>
                            Invite this address with the Admin role: <strong>{KLICKLY_EMAIL}</strong>
                        </span>
                        <StyledCopyButton type='button' data-e2e='tracking_tw_invite_copy_btn' onClick={copyEmail}>
                            <Icon.CopyIcon />
                            {copied ? 'Copied' : 'Copy'}
                        </StyledCopyButton>
                    </Flex>
                </li>
                <li>Come back and confirm. We’ll create the key and connect Spend within 1 business day.</li>
            </StyledSteps>
            <Flex align='center' gap={24} wrap>
                <Button
                    data-e2e='tracking_tw_invite_sent_btn'
                    type='primary'
                    loading={submitting}
                    onClick={() => setInviteSent(true)}
                >
                    I’ve sent the invite
                </Button>
                <Typography.Text type='body2' color={COLORS.$gray60Black}>
                    You can remove Klickly’s access at any time; Spend syncing stops if you do.
                </Typography.Text>
            </Flex>
        </React.Fragment>
    );
};

export default KlicklyTab;
